import React, { useState, useRef } from "react";
import { db } from "../config/firebase";
import {
  collection,
  doc,
  Timestamp,
  writeBatch,
  getDoc,
  getDocs,
} from "firebase/firestore";
import * as XLSX from "xlsx";
import moment from "moment";
import { formatDate } from "../utils/formatDate";
import {
  Button,
  CircularProgress,
  Snackbar,
  Alert,
  Box,
  LinearProgress,
  Typography,
} from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileArrowDown, faUpload } from "@fortawesome/free-solid-svg-icons";
import { faFileExcel } from "@fortawesome/pro-solid-svg-icons";
import { downloadExcelTemplate } from "../utils/exportTemplate";

const EditUploadOptimized = ({
  collectionName = "TpStudents",
  onActionComplete,
  exportData,
  exportColumnOrder,
  booleanFields = [],
  templateFields,
}) => {
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({
    show: false,
    current: 0,
    total: 0,
    phase: "",
    processed: 0,
    updated: 0,
    added: 0,
    skipped: 0,
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const fileInputRef = useRef(null);

  const formatDateInternal = (dateValue) => {
    if (!dateValue && typeof dateValue !== "number") return null;

    if (dateValue instanceof Date) {
      if (isNaN(dateValue.getTime())) return null;
      return Timestamp.fromDate(dateValue);
    }

    if (typeof dateValue === "number") {
      // Improved Excel date conversion
      const excelEpoch = new Date(1900, 0, 1);
      const millisecondsInDay = 86400000;
      const adjustedDays = dateValue - 2; // Account for Excel's leap year bug
      const date = new Date(
        excelEpoch.getTime() + adjustedDays * millisecondsInDay
      );

      if (isNaN(date.getTime())) {
        console.warn(
          "Numeric value did not convert to a valid date:",
          dateValue
        );
        return null;
      }

      // Set to noon UTC to avoid timezone issues
      const utcDate = new Date(
        Date.UTC(
          date.getFullYear(),
          date.getMonth(),
          date.getDate(),
          12,
          0,
          0,
          0
        )
      );

      return Timestamp.fromDate(utcDate);
    }

    if (typeof dateValue === "string") {
      const formats = [
        "YYYY-MM-DD",
        "MM/DD/YYYY",
        "DD/MM/YYYY",
        "YYYY-MM-DD HH:mm:ss",
        "MM/DD/YYYY HH:mm:ss",
      ];

      let formattedDate = moment(dateValue, formats, true);
      if (!formattedDate.isValid()) {
        formattedDate = moment(dateValue);
      }

      if (formattedDate.isValid()) {
        formattedDate.utc().hour(12).minute(0).second(0).millisecond(0);
        return Timestamp.fromDate(formattedDate.toDate());
      }
    }

    console.warn("Failed to parse date value:", dateValue);
    return null;
  };

  const valuesAreEqual = (newValue, existingValue) => {
    if (
      (newValue === null || newValue === undefined) &&
      (existingValue === null || existingValue === undefined)
    ) {
      return true;
    }
    if (
      newValue === null ||
      newValue === undefined ||
      existingValue === null ||
      existingValue === undefined
    ) {
      return false;
    }

    if (newValue instanceof Timestamp && existingValue instanceof Timestamp) {
      return newValue.seconds === existingValue.seconds;
    }

    if (typeof newValue === "number" && typeof existingValue === "number") {
      return newValue === existingValue;
    }

    return String(newValue) === String(existingValue);
  };

  const hasDataChanged = (newData, existingData) => {
    const allFields = new Set([
      ...Object.keys(newData).filter(
        (key) => key !== "updatedAt" && key !== "created_at"
      ),
      ...Object.keys(existingData).filter(
        (key) => key !== "updatedAt" && key !== "created_at"
      ),
    ]);

    for (const key of allFields) {
      if (!valuesAreEqual(newData[key], existingData[key])) {
        return true;
      }
    }
    return false;
  };

  const processDataInChunks = async (jsonData, chunkSize = 500) => {
    const totalRecords = jsonData.length;
    let updateCount = 0;
    let addCount = 0;
    let skippedCount = 0;
    let errorRows = [];

    setUploadProgress({
      show: true,
      current: 0,
      total: totalRecords,
      phase: "Starting upload...",
      processed: 0,
      updated: 0,
      added: 0,
      skipped: 0,
    });

    // Process in chunks for better performance
    for (
      let chunkStart = 0;
      chunkStart < totalRecords;
      chunkStart += chunkSize
    ) {
      const chunkEnd = Math.min(chunkStart + chunkSize, totalRecords);
      const chunk = jsonData.slice(chunkStart, chunkEnd);

      setUploadProgress((prev) => ({
        ...prev,
        phase: `Processing chunk ${
          Math.floor(chunkStart / chunkSize) + 1
        } of ${Math.ceil(totalRecords / chunkSize)}`,
        current: chunkStart,
      }));

      const chunkIds = [];
      const chunkProcessedData = [];

      // Pre-process chunk to extract data and IDs
      for (const [localIndex, rowFromExcel] of chunk.entries()) {
        const globalIndex = chunkStart + localIndex;
        try {
          const docData = {};
          let docId = null;

          for (const excelHeader in rowFromExcel) {
            if (!rowFromExcel.hasOwnProperty(excelHeader)) continue;

            const firestoreField = excelHeader
              .replace(/ /g, "_")
              .replace(/[^a-zA-Z0-9_]/gi, "");
            let value = rowFromExcel[excelHeader];

            if (firestoreField === "id") {
              docId =
                value !== null && value !== undefined
                  ? String(value).trim()
                  : null;
              if (docId === "") docId = null;
              continue;
            }

            if (
              value === "NULL" ||
              value === null ||
              value === undefined ||
              value === ""
            ) {
              docData[firestoreField] = null;
              continue;
            }

            // Date field processing
            const dateHeaders = [
              "application_dt",
              "dob",
              "last_updated",
              "added_to_portal",
              "created_at",
            ];

            if (
              dateHeaders.includes(firestoreField.toLowerCase()) ||
              firestoreField.toLowerCase().includes("date") ||
              firestoreField.toLowerCase().includes("dt")
            ) {
              const ts = formatDateInternal(value);
              docData[firestoreField] = ts;
            } else {
              docData[firestoreField] = String(value);
            }
          }

          chunkProcessedData.push({ docId, docData, globalIndex });
          if (docId) chunkIds.push(docId);
        } catch (error) {
          errorRows.push({ row: globalIndex + 2, error: error.message });
        }
      }

      // Batch fetch existing documents
      const existingDocsMap = new Map();
      if (chunkIds.length > 0) {
        const fetchSize = 10; // Firestore batch read limit
        for (let i = 0; i < chunkIds.length; i += fetchSize) {
          const fetchChunk = chunkIds.slice(i, i + fetchSize);
          const promises = fetchChunk.map((id) =>
            getDoc(doc(db, collectionName, id))
          );
          const docSnaps = await Promise.all(promises);

          docSnaps.forEach((docSnap, index) => {
            if (docSnap.exists()) {
              existingDocsMap.set(fetchChunk[index], docSnap.data());
            }
          });
        }
      }

      // Create batch operations
      let currentBatch = writeBatch(db);
      let operationCount = 0;

      for (const { docId, docData, globalIndex } of chunkProcessedData) {
        try {
          if (docId) {
            const docRef = doc(db, collectionName, docId);
            const existingData = existingDocsMap.get(docId);

            if (existingData) {
              if (hasDataChanged(docData, existingData)) {
                docData["updatedAt"] = Timestamp.now();
                currentBatch.set(docRef, docData, { merge: true });
                updateCount++;
                operationCount++;
              } else {
                skippedCount++;
              }
            } else {
              docData["created_at"] = Timestamp.now();
              docData["updatedAt"] = Timestamp.now();
              currentBatch.set(docRef, docData);
              addCount++;
              operationCount++;
            }
          } else {
            docData["created_at"] = Timestamp.now();
            docData["updatedAt"] = Timestamp.now();
            const newDocRef = doc(collection(db, collectionName));
            currentBatch.set(newDocRef, docData);
            addCount++;
            operationCount++;
          }

          if (operationCount >= 450) {
            await currentBatch.commit();
            currentBatch = writeBatch(db);
            operationCount = 0;
          }
        } catch (error) {
          errorRows.push({ row: globalIndex + 2, error: error.message });
        }
      }

      if (operationCount > 0) {
        await currentBatch.commit();
      }

      setUploadProgress((prev) => ({
        ...prev,
        current: chunkEnd,
        processed: chunkEnd,
        updated: updateCount,
        added: addCount,
        skipped: skippedCount,
      }));

      // Small delay to prevent overwhelming the UI
      await new Promise((resolve) => setTimeout(resolve, 10));
    }

    return { updateCount, addCount, skippedCount, errorRows };
  };

  const handleExportTemplate = () => {
    const fields =
      templateFields && templateFields.length > 0
        ? templateFields
        : exportColumnOrder || [];
    if (fields.length === 0) {
      setSnackbar({
        open: true,
        message: "No fields configured for this template.",
        severity: "info",
      });
      return;
    }
    downloadExcelTemplate(fields, `${collectionName}-template`);
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setLoading(true);
    setSnackbar({ open: false, message: "", severity: "info" });

    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const fileData = new Uint8Array(e.target.result);
        const workbook = XLSX.read(fileData, {
          type: "array",
          cellDates: true,
        });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { raw: false });

        if (jsonData.length === 0) {
          setSnackbar({
            open: true,
            message: "The uploaded file is empty or has no data.",
            severity: "warning",
          });
          setLoading(false);
          return;
        }

        const { updateCount, addCount, skippedCount, errorRows } =
          await processDataInChunks(jsonData);

        setUploadProgress((prev) => ({ ...prev, show: false }));

        let summaryMsg = `Upload complete! Updated: ${updateCount}, Added: ${addCount}, Skipped: ${skippedCount}`;
        if (errorRows.length > 0) {
          summaryMsg += `. Errors: ${errorRows.length}`;
          console.error("Upload errors:", errorRows);
        }

        setSnackbar({
          open: true,
          message: summaryMsg,
          severity: errorRows.length > 0 ? "warning" : "success",
        });

        if (onActionComplete) {
          onActionComplete(errorRows.length === 0, summaryMsg);
        }
      } catch (error) {
        console.error("Error processing file:", error);
        setUploadProgress((prev) => ({ ...prev, show: false }));
        setSnackbar({
          open: true,
          message: `Error: ${error.message}`,
          severity: "error",
        });
      } finally {
        setLoading(false);
      }
    };

    reader.readAsArrayBuffer(file);
  };

  const handleExport = async () => {
    setLoading(true);
    try {
      let dataForSheet = [];

      if (exportData && exportData.length > 0) {
        dataForSheet = exportData.map((doc) => {
          const rowData = {};
          if (doc.id) rowData.id = doc.id;

          if (exportColumnOrder && Array.isArray(exportColumnOrder)) {
            exportColumnOrder.forEach((key) => {
              if (key === "created_at" || key === "id") return;
              if (doc[key] instanceof Timestamp) {
                rowData[key] = formatDate(doc[key]);
              } else {
                rowData[key] = doc[key];
              }
            });

            Object.keys(doc).forEach((key) => {
              if (
                key === "created_at" ||
                key === "id" ||
                exportColumnOrder.includes(key)
              )
                return;
              if (doc[key] instanceof Timestamp) {
                rowData[key] = formatDate(doc[key]);
              } else {
                rowData[key] = doc[key];
              }
            });
          } else {
            for (const key in doc) {
              if (key === "created_at" || key === "id") continue;
              if (doc[key] instanceof Timestamp) {
                rowData[key] = formatDate(doc[key]);
              } else {
                rowData[key] = doc[key];
              }
            }
          }
          return rowData;
        });
      } else {
        const querySnapshot = await getDocs(collection(db, collectionName));
        dataForSheet = querySnapshot.docs.map((docSnapshot) => {
          const data = docSnapshot.data();
          const rowData = { id: docSnapshot.id };

          for (const key in data) {
            if (key === "created_at") continue;
            if (data[key] instanceof Timestamp) {
              rowData[key] = formatDate(data[key]);
            } else {
              rowData[key] = data[key];
            }
          }
          return rowData;
        });
      }

      if (dataForSheet.length === 0) {
        setSnackbar({
          open: true,
          message: "No data to export.",
          severity: "info",
        });
        return;
      }

      const worksheet = XLSX.utils.json_to_sheet(dataForSheet);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
      XLSX.writeFile(workbook, `${collectionName}.xlsx`);

      setSnackbar({
        open: true,
        message: "Data exported successfully!",
        severity: "success",
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: `Export error: ${error.message}`,
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box p={2}>
      <Button
        variant="contained"
        component="label"
        startIcon={<FontAwesomeIcon icon={faUpload} />}
        disabled={loading || uploadProgress.show}
        sx={{ mr: 2 }}
      >
        {loading ? <CircularProgress size={24} /> : "Upload Excel File"}
        <input
          type="file"
          accept=".xlsx,.xls"
          onChange={handleFileUpload}
          style={{ display: "none" }}
          ref={fileInputRef}
        />
      </Button>

      <Button
        variant="contained"
        onClick={handleExport}
        startIcon={<FontAwesomeIcon icon={faFileArrowDown} />}
        disabled={loading}
        sx={{
          backgroundColor: "green",
          color: "white",
          "&:hover": { backgroundColor: "#228B22" },
        }}
      >
        {loading ? <CircularProgress size={24} /> : "Export to Excel"}
      </Button>

      <Button
        variant="contained"
        color="success"
        onClick={handleExportTemplate}
        startIcon={<FontAwesomeIcon icon={faFileExcel} />}
        disabled={loading}
        sx={{ ml: 2, color: "white" }}
      >
        Export Template
      </Button>

      {uploadProgress.show && (
        <Box mt={2} p={2} border={1} borderColor="grey.300" borderRadius={1}>
          <Typography variant="h6" gutterBottom>
            Upload Progress: {uploadProgress.phase}
          </Typography>
          <Typography variant="body2" gutterBottom>
            Processed: {uploadProgress.processed} / {uploadProgress.total}{" "}
            records
          </Typography>
          <LinearProgress
            variant="determinate"
            value={(uploadProgress.processed / uploadProgress.total) * 100}
            sx={{ mb: 1 }}
          />
          <Typography variant="body2">
            Updated: {uploadProgress.updated} | Added: {uploadProgress.added} |
            Skipped: {uploadProgress.skipped}
          </Typography>
        </Box>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default EditUploadOptimized;
